/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Advanced management interface for Mercenary units with combat power analytics and squad deployment.
 */

import React, { useState } from "react";
import {
  Shield,
  Swords,
  Crosshair,
  Filter,
  ArrowUpDown,
  Skull,
  X,
  Check,
  Users,
  ChevronLeft,
  Plus,
} from "lucide-react";
import { callBackend } from "../utils/api";

const RARITY_COLORS = {
  common: "border-zinc-800 text-zinc-400 bg-zinc-900/50",
  rare: "border-blue-600 text-blue-400 bg-blue-900/10",
  epic: "border-purple-600 text-purple-400 bg-purple-900/10",
  legendary:
    "border-red-600 text-red-500 bg-red-900/10 shadow-[0_0_15px_rgba(220,38,38,0.2)]",
};

export default function ThugsManagement({
  inventory,
  onBack,
  onEquip,
  fetchStats,
}) {
  const [rarityFilter, setRarityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("power_desc");
  const [selectionTarget, setSelectionTarget] = useState(null); // { unit, slot }
  const [managedStackId, setManagedStackId] = useState(null);
  const [isSplitting, setIsSplitting] = useState(false);

  const thugs = inventory.filter((i) => i.type === "mercenary");

  // Dashboard Stats
  const totalAtk = thugs.reduce(
    (acc, t) => acc + (t.atk_power || 0) * t.quantity,
    0,
  );
  const totalDef = thugs.reduce(
    (acc, t) => acc + (t.def_power || 0) * t.quantity,
    0,
  );

  const getEquippedName = (id) => {
    if (!id) return "Empty Slot";
    const item = inventory.find((i) => i.id === id);
    return item ? item.name : "Unknown Item";
  };

  const handleEquipClick = (unit, slot) => {
    setSelectionTarget({ unit, slot });
  };

  const handleSplitUnit = async (inventoryId) => {
    if (isSplitting) return;
    setIsSplitting(true);
    try {
      await callBackend("/mercenaries/split", {
        method: "POST",
        body: { inventoryId },
      });
      if (typeof fetchStats === "function") {
        await fetchStats();
      }
    } catch (err) {
      console.error("Split failed:", err);
    } finally {
      setIsSplitting(false);
    }
  };

  const filteredThugs = thugs
    .filter((t) => rarityFilter === "all" || t.rarity === rarityFilter)
    .sort((a, b) => {
      if (sortBy === "power_desc")
        return b.atk_power + b.def_power - (a.atk_power + a.def_power);
      if (sortBy === "atk_desc") return b.atk_power - a.atk_power;
      if (sortBy === "def_desc") return b.def_power - a.def_power;
      if (sortBy === "qty") return b.quantity - a.quantity;
      return 0;
    });

  // --- SQUAD VIEW LOGIC ---
  const activeStack = managedStackId
    ? thugs.find((t) => t.id === managedStackId)
    : null;
  const individualUnits = activeStack
    ? inventory.filter(
        (i) => i.item_id === activeStack.item_id && i.quantity === 1,
      )
    : [];

  if (activeStack) {
    return (
      <div className="space-y-6 animate-in fade-in duration-300">
        <div className="flex items-center justify-between border-b border-red-900 pb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setManagedStackId(null)}
              className="p-2 bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <div>
              <h2 className="text-2xl font-black italic text-white uppercase leading-none">
                Squad: {activeStack.name}
              </h2>
              <p className="text-[10px] text-red-500 font-mono uppercase tracking-widest mt-1">
                Individual Unit Configuration
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[8px] text-zinc-500 uppercase font-black">
              Reserve Size
            </p>
            <p className="text-xl font-black text-white">
              x{activeStack.quantity}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Deployment Button */}
          <div className="border-2 border-dashed border-zinc-800 bg-zinc-900/20 p-6 flex flex-col items-center justify-center text-center space-y-4 hover:border-red-600/50 transition-all group">
            <div className="w-12 h-12 rounded-full border-2 border-zinc-800 flex items-center justify-center group-hover:border-red-600 transition-colors">
              <Plus className="text-zinc-600 group-hover:text-red-600" />
            </div>
            <div>
              <p className="text-xs font-bold text-white uppercase">
                Deploy Individual
              </p>
              <p className="text-[10px] text-zinc-500 uppercase italic leading-tight mt-1">
                Pull 1 unit from the stack to
                <br />
                equip uniquely
              </p>
            </div>
            <button
              onClick={() => handleSplitUnit(activeStack.id)}
              disabled={isSplitting || activeStack.quantity <= 1}
              className="w-full py-2 bg-red-600 text-black font-black text-[10px] uppercase italic hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSplitting ? "Deploying..." : "Confirm Deployment"}
            </button>
          </div>

          {/* Individual Units */}
          {individualUnits.map((unit, idx) => (
            <div
              key={unit.id}
              className={`border-2 p-4 flex flex-col justify-between ${RARITY_COLORS[unit.rarity] || RARITY_COLORS.common}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-[8px] font-black uppercase px-2 py-0.5 border border-white/10 bg-black">
                    Unit #{idx + 1}
                  </span>
                  <h4 className="font-black text-white uppercase italic text-sm mt-2">
                    {unit.name}
                  </h4>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  onClick={() => handleEquipClick(unit, "weapon")}
                  className="bg-black/40 border border-dashed border-zinc-700 p-2 hover:border-red-500 transition-all text-left"
                >
                  <p className="text-[8px] text-zinc-500 uppercase font-bold">
                    Weapon
                  </p>
                  <p className="text-[10px] text-white truncate">
                    {getEquippedName(unit.assigned_weapon_id)}
                  </p>
                </button>
                <button
                  onClick={() => handleEquipClick(unit, "armor")}
                  className="bg-black/40 border border-dashed border-zinc-700 p-2 hover:border-blue-500 transition-all text-left"
                >
                  <p className="text-[8px] text-zinc-500 uppercase font-bold">
                    Armor
                  </p>
                  <p className="text-[10px] text-white truncate">
                    {getEquippedName(unit.assigned_armor_id)}
                  </p>
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-auto">
                <div className="bg-zinc-900/50 p-1 border border-zinc-800 text-center">
                  <span className="text-[8px] text-zinc-500 font-bold uppercase">
                    ATK
                  </span>
                  <p className="text-sm font-black text-white">
                    {unit.atk_power}
                  </p>
                </div>
                <div className="bg-zinc-900/50 p-1 border border-zinc-800 text-center">
                  <span className="text-[8px] text-zinc-500 font-bold uppercase">
                    DEF
                  </span>
                  <p className="text-sm font-black text-white">
                    {unit.def_power}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Equip Modal */}
        {selectionTarget && (
          <SelectionModal
            selectionTarget={selectionTarget}
            setSelectionTarget={setSelectionTarget}
            inventory={inventory}
            onEquip={onEquip}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
      {/* Equip Modal for stack view */}
      {selectionTarget && (
        <SelectionModal
          selectionTarget={selectionTarget}
          setSelectionTarget={setSelectionTarget}
          inventory={inventory}
          onEquip={onEquip}
          isStack={true}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between border-b border-red-900 pb-4">
        <div>
          <h2 className="text-3xl font-black italic text-red-600 uppercase flex items-center gap-2 leading-none">
            <Swords size={28} /> Active Muscle
          </h2>
          <p className="text-[10px] text-red-400 font-mono uppercase tracking-[0.3em] mt-1">
            Security & Enforcement Division
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-white transition-all uppercase font-black text-[10px] italic"
        >
          &larr; Return to Trap
        </button>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-black border border-red-900/50 p-4 flex flex-col justify-between group relative overflow-hidden">
          <div className="relative z-10">
            <p className="text-[10px] text-red-700 uppercase font-bold flex items-center gap-1">
              <Skull size={12} /> Total Soldiers
            </p>
            <p className="text-2xl font-black text-white">
              {thugs.reduce((acc, t) => acc + t.quantity, 0).toLocaleString()}
            </p>
          </div>
          <Skull
            className="absolute -right-4 -bottom-4 text-red-900/20 group-hover:text-red-900/30 transition-all transform rotate-12"
            size={80}
          />
        </div>

        <div className="bg-black border border-zinc-800 p-4">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
                <Crosshair size={12} className="text-red-500" /> Offensive Power
              </p>
              <p className="text-xl font-black text-white">
                {totalAtk.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="w-full h-1 bg-zinc-900 mt-2">
            <div
              className="h-full bg-red-600 transition-all duration-500"
              style={{
                width: `${Math.min(100, (totalAtk / (totalAtk + totalDef || 1)) * 100)}%`,
              }}
            />
          </div>
        </div>

        <div className="bg-black border border-zinc-800 p-4">
          <div className="flex justify-between items-end">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-bold flex items-center gap-1">
                <Shield size={12} className="text-blue-500" /> Defensive Rating
              </p>
              <p className="text-xl font-black text-white">
                {totalDef.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="w-full h-1 bg-zinc-900 mt-2">
            <div
              className="h-full bg-blue-600 transition-all duration-500"
              style={{
                width: `${Math.min(100, (totalDef / (totalAtk + totalDef || 1)) * 100)}%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Control Bar */}
      <div className="flex flex-wrap gap-4 bg-zinc-950 p-3 border border-zinc-800 items-center">
        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <Filter size={14} className="text-zinc-500" />
          <select
            value={rarityFilter}
            onChange={(e) => setRarityFilter(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer font-bold"
          >
            <option value="all">All Ranks</option>
            <option value="common">Common</option>
            <option value="rare">Rare</option>
            <option value="epic">Epic</option>
            <option value="legendary">Legendary</option>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <ArrowUpDown size={14} className="text-zinc-500" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer font-bold"
          >
            <option value="power_desc">Overall Power</option>
            <option value="atk_desc">Attack Focused</option>
            <option value="def_desc">Defense Focused</option>
            <option value="qty">Horde Size</option>
          </select>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filteredThugs.map((unit) => (
          <div
            key={unit.id}
            className={`border-2 p-4 flex flex-col justify-between group hover:scale-[1.02] transition-all ${
              RARITY_COLORS[unit.rarity] || RARITY_COLORS.common
            }`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <span
                  className={`text-[8px] font-black uppercase px-2 py-0.5 border ${RARITY_COLORS[unit.rarity]?.split(" ")[0]} bg-black`}
                >
                  {unit.rarity}
                </span>
                <h4 className="font-black text-white uppercase italic text-sm mt-2">
                  {unit.name}
                </h4>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs font-black text-white bg-black/50 px-2 py-1 border border-zinc-700">
                  x{unit.quantity}
                </span>
                {unit.quantity > 1 && (
                  <button
                    onClick={() => setManagedStackId(unit.id)}
                    className="text-[8px] font-black uppercase text-red-500 hover:text-white transition-colors flex items-center gap-1"
                  >
                    <Users size={10} /> Manage Squad
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => handleEquipClick(unit, "weapon")}
                className="bg-black/40 border border-dashed border-zinc-700 p-2 hover:border-red-500 transition-all text-left"
              >
                <p className="text-[8px] text-zinc-500 uppercase font-bold">
                  Weapon
                </p>
                <p className="text-[10px] text-white truncate">
                  {getEquippedName(unit.assigned_weapon_id)}
                </p>
              </button>
              <button
                onClick={() => handleEquipClick(unit, "armor")}
                className="bg-black/40 border border-dashed border-zinc-700 p-2 hover:border-blue-500 transition-all text-left"
              >
                <p className="text-[8px] text-zinc-500 uppercase font-bold">
                  Armor
                </p>
                <p className="text-[10px] text-white truncate">
                  {getEquippedName(unit.assigned_armor_id)}
                </p>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-auto">
              <div className="bg-zinc-900/50 p-2 border border-zinc-800 text-center">
                <span className="text-[9px] text-zinc-500 font-bold uppercase flex items-center justify-center gap-1">
                  <Crosshair size={10} className="text-red-500" /> ATK
                </span>
                <p className="text-lg font-black text-white">
                  {unit.atk_power}
                </p>
              </div>
              <div className="bg-zinc-900/50 p-2 border border-zinc-800 text-center">
                <span className="text-[9px] text-zinc-500 font-bold uppercase flex items-center justify-center gap-1">
                  <Shield size={10} className="text-blue-500" /> DEF
                </span>
                <p className="text-lg font-black text-white">
                  {unit.def_power}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sub-component for selection logic to clean up the main file
function SelectionModal({
  selectionTarget,
  setSelectionTarget,
  inventory,
  onEquip,
  isStack = false,
}) {
  return (
    <div className="fixed inset-0 z-[6000] bg-black/90 flex items-center justify-center p-4">
      <div className="bg-zinc-950 border-2 border-red-900 w-full max-w-md max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-red-900 flex justify-between items-center bg-red-900/10">
          <h3 className="font-black italic uppercase text-white">
            {isStack ? "Equip Squad" : `Equip ${selectionTarget.slot}`}
          </h3>
          <button
            onClick={() => setSelectionTarget(null)}
            className="text-zinc-500 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {isStack && (
          <div className="p-3 bg-zinc-900/50 border-b border-zinc-800">
            <p className="text-[10px] text-yellow-500 uppercase font-black">
              Mass Assignment
            </p>
            <p className="text-[9px] text-zinc-400 italic">
              This will equip this item to all {selectionTarget.unit.quantity}{" "}
              units in the squad.
            </p>
          </div>
        )}

        <div className="overflow-y-auto p-2 space-y-2">
          <button
            onClick={() => {
              onEquip(selectionTarget.unit.id, null, selectionTarget.slot);
              setSelectionTarget(null);
            }}
            className="w-full bg-black border border-zinc-800 p-3 text-left text-[10px] font-black text-red-500 hover:border-red-500 uppercase italic transition-all"
          >
            Unequip Current
          </button>

          {inventory
            .filter(
              (i) =>
                i.type === selectionTarget.slot &&
                i.quantity >= (isStack ? selectionTarget.unit.quantity : 1),
            )
            .map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  onEquip(
                    selectionTarget.unit.id,
                    item.id,
                    selectionTarget.slot,
                  );
                  setSelectionTarget(null);
                }}
                className="w-full bg-zinc-900 border border-zinc-800 p-3 flex justify-between items-center hover:border-white transition-all group"
              >
                <div className="text-left">
                  <p className="text-white font-black uppercase text-[10px] italic">
                    {item.name}
                  </p>
                  <p className="text-[8px] text-zinc-500 uppercase font-bold mt-1">
                    {selectionTarget.slot === "weapon"
                      ? `ATK: +${item.atk_power}`
                      : `DEF: +${item.def_power}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[8px] text-zinc-600 uppercase font-black">
                    Stock: {item.quantity}
                  </p>
                  {selectionTarget.unit[
                    selectionTarget.slot === "weapon"
                      ? "assigned_weapon_id"
                      : "assigned_armor_id"
                  ] === item.id && (
                    <Check size={14} className="text-green-500 mt-1 ml-auto" />
                  )}
                </div>
              </button>
            ))}

          {inventory.filter((i) => i.type === selectionTarget.slot).length ===
            0 && (
            <div className="p-8 text-center text-zinc-700 text-[10px] font-black uppercase italic">
              No tactical gear in stock.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
